import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import { ArrowBack, FilterList } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getProductTracelogs } from 'api/catalog/get-product-tracelogs';
import { getProduct } from 'api/catalog/get-product';
import { ProductVm } from 'api/catalog/product.vm';
import { ProductTracelogType, ProductTracelogVm } from 'api/catalog/product-tracelog.vm';

const ProductTraceabilityPage = () => {
  const navigate = useNavigate();
  const { productId, batchOrSerialNumber } = useParams<{
    productId: string;
    batchOrSerialNumber: string;
  }>();
  const [product, setProduct] = useState<ProductVm | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [tracelogs, setTracelogs] = useState<ProductTracelogVm[]>([]);
  const [tracelogsLoading, setTracelogsLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchProductDetails = () => {
    if (!productId) return;
    setProductLoading(true);
    getProduct(productId, {
      success: (response: ProductVm) => {
        setProduct(response);
        setProductLoading(false);
      },
      error: (err) => {
        console.error('Falha ao carregar detalhes do produto:', err);
        setProductLoading(false);
      },
    });
  };

  const fetchTraceabilityRecords = async (reset = false) => {
    if (!productId || !batchOrSerialNumber) return;

    const currentOffset = reset ? '' : nextOffset || '';
    setTracelogsLoading(true);

    getProductTracelogs(productId, batchOrSerialNumber, currentOffset, {
      success: (response) => {
        if (reset) {
          setTracelogs(response.objects);
        } else {
          setTracelogs((prev) => [...prev, ...response.objects]);
        }
        setNextOffset(response.nextOffset);
        setHasMore(response.nextOffset !== null);
        setTracelogsLoading(false);
      },
      error: (err) => {
        console.error('Falha ao carregar registos de rastreabilidade:', err);
        setTracelogsLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  useEffect(() => {
    if (product) {
      fetchTraceabilityRecords(true);
    }
  }, [product, batchOrSerialNumber]);

  const handleLoadMore = () => {
    if (hasMore && !tracelogsLoading) {
      fetchTraceabilityRecords();
    }
  };

  const getTypeChip = (type: ProductTracelogType) => {
    const colorMap = {
      [ProductTracelogType.CREATED]: 'primary',
      [ProductTracelogType.RECEIVED]: 'success',
      [ProductTracelogType.RECEPTION_CANCELED]: 'error',
      [ProductTracelogType.PICKED]: 'warning',
      [ProductTracelogType.PICK_CANCELED]: 'error',
      [ProductTracelogType.MOVEMENT]: 'info',
    };

    // Translate event types
    const labelMap: Record<ProductTracelogType, string> = {
      [ProductTracelogType.CREATED]: 'Criado',
      [ProductTracelogType.RECEIVED]: 'Recebido',
      [ProductTracelogType.RECEPTION_CANCELED]: 'Receção Cancelada',
      [ProductTracelogType.PICKED]: 'Picking',
      [ProductTracelogType.PICK_CANCELED]: 'Picking Cancelado',
      [ProductTracelogType.MOVEMENT]: 'Movimentação',
    };

    return <Chip label={labelMap[type] || type} color={colorMap[type] as any} size="small" />;
  };

  if (productLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="error">
          Falha ao carregar detalhes do produto
        </Typography>
      </Box>
    );
  }

  if (!product.usesBatch && !product.usesSerialNumber) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" onClick={() => navigate('/products/products')}>
              Produtos
            </Link>
            <Link underline="hover" color="inherit" onClick={() => navigate(`/products/products/${productId}`)}>
              Detalhes do Produto
            </Link>
            <Typography color="text.primary">Rastreabilidade</Typography>
          </Breadcrumbs>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Rastreabilidade Não Disponível
          </Typography>
          <Typography>
            Este produto não utiliza lotes nem números de série, por isso a informação de rastreabilidade não está disponível.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={() => navigate('/products/products')}>
            Produtos
          </Link>
          <Link underline="hover" color="inherit" onClick={() => navigate(`/products/products/${productId}`)}>
            Detalhes do Produto
          </Link>
          <Typography color="text.primary">Rastreabilidade</Typography>
        </Breadcrumbs>
      </Box>

      <Paper sx={{ mb: 3, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h5" gutterBottom>
              {product.name}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>{product.usesBatch ? 'Número de Lote' : 'Número de Série'}:</strong> {batchOrSerialNumber}
            </Typography>
          </Box>
          <IconButton>
            <FilterList />
          </IconButton>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Eventos de Rastreabilidade
      </Typography>

      {tracelogsLoading && tracelogs.length === 0 ? (
        <CircularProgress />
      ) : tracelogs.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tipo de Evento</TableCell>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Detalhes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tracelogs.map((tracelog, index) => (
                  <TableRow key={`${index}`}>
                    <TableCell>{getTypeChip(tracelog.type)}</TableCell>
                    <TableCell>{new Date(tracelog.ocurredAt).toLocaleString('pt-PT')}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => console.log('Ver detalhes', tracelog)}>
                        Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={tracelogsLoading}
                startIcon={tracelogsLoading ? <CircularProgress size={20} /> : null}
              >
                {tracelogsLoading ? 'A carregar...' : 'Carregar mais'}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Typography variant="body2" color="textSecondary">
          Nenhum registo de rastreabilidade encontrado
        </Typography>
      )}
    </Box>
  );
};

export default ProductTraceabilityPage;
